import React, {useEffect, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {getSubmissions, removeSubmission} from '../data/submissions';

const {FiArrowLeft, FiLogOut, FiTrash2, FiDownload, FiInbox, FiCopy, FiCheck} = FiIcons;

const AdminDashboard = ({onLogout, onBack}) => {
  const [submissions, setSubmissions] = useState([]);
  const [copiedId, setCopiedId] = useState('');

  useEffect(() => {
    setSubmissions(getSubmissions());
  }, []);

  const deleteSubmission = (id) => {
    removeSubmission(id);
    setSubmissions((current) => current.filter((submission) => submission.id !== id));
  };

  const downloadSubmission = (submission) => {
    const file = new Blob([JSON.stringify(submission, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${submission.answers.businessName || 'submission'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyPrompt = async (submission) => {
    try {
      await navigator.clipboard.writeText(submission.prompt || '');
      setCopiedId(submission.id);
      window.setTimeout(() => setCopiedId(''), 2000);
    } catch (error) {
      console.error('Unable to copy saved prompt:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <header className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-500 mb-4"
            >
              <SafeIcon icon={FiArrowLeft} /> Questionnaire
            </button>
            <h1 className="text-4xl font-bold text-gray-900">Submissions</h1>
            <p className="text-gray-500 mt-2">{submissions.length} saved locally</p>
          </div>

          <button
            onClick={onLogout}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <SafeIcon icon={FiLogOut} /> Sign out
          </button>
        </header>

        {submissions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
            <SafeIcon icon={FiInbox} className="text-gray-300 text-5xl mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800">No submissions yet</h2>
            <p className="text-gray-500 mt-2">Completed questionnaires will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map((submission) => (
              <article
                key={submission.id}
                className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm"
              >
                <div className="flex flex-wrap justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {submission.answers.businessName || 'Unnamed business'}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {submission.answers.businessEmail || 'No email'} ·{' '}
                      {new Date(submission.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadSubmission(submission)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Download"
                    >
                      <SafeIcon icon={FiDownload} />
                    </button>
                    <button
                      onClick={() => deleteSubmission(submission.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>

                <details className="mt-5">
                  <summary className="cursor-pointer text-sm font-semibold text-gray-700">
                    View full submission
                  </summary>
                  <pre className="mt-4 whitespace-pre-wrap bg-gray-50 rounded-xl p-4 text-xs text-gray-700 overflow-auto">
                    {JSON.stringify(submission.answers, null, 2)}
                  </pre>
                </details>

                <details className="mt-4">
                  <summary className="cursor-pointer text-sm font-semibold text-gray-700">
                    View generated prompt
                  </summary>
                  <div className="mt-4 relative">
                    <button
                      onClick={() => copyPrompt(submission)}
                      className="absolute top-3 right-3 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold flex items-center gap-2 hover:bg-gray-50"
                    >
                      <SafeIcon icon={copiedId === submission.id ? FiCheck : FiCopy} />
                      {copiedId === submission.id ? 'Copied' : 'Copy prompt'}
                    </button>
                    <pre className="whitespace-pre-wrap bg-gray-50 rounded-xl p-5 pt-14 text-xs text-gray-700 overflow-auto max-h-96">
                      {submission.prompt || 'No generated prompt was saved for this submission.'}
                    </pre>
                  </div>
                </details>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;